import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  BarChart3, 
  Download,
  Database,
  FileText,
  PieChart,
  Activity,
  Award,
  Target,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

const AdvancedAnalytics = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('executive');
  
  // Executive Dashboard State
  const [executiveDashboard, setExecutiveDashboard] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [period, setPeriod] = useState('month');
  
  // Analytics State
  const [cohortAnalysis, setCohortAnalysis] = useState([]);
  const [guestLifetimeValue, setGuestLifetimeValue] = useState([]);
  const [channelAttribution, setChannelAttribution] = useState(null);
  const [marketBasket, setMarketBasket] = useState(null);
  const [trends, setTrends] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  
  // Custom Reports State
  const [customReports, setCustomReports] = useState([]);
  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('revenue');
  const [reportFilters, setReportFilters] = useState({});
  
  // Data Warehouse State
  const [warehouseStatus, setWarehouseStatus] = useState(null);

  useEffect(() => {
    if (selectedProperty) {
      fetchExecutiveDashboard();
      fetchKPIs();
    }
  }, [selectedProperty, period]);

  const getToken = () => localStorage.getItem('access_token');

  const fetchExecutiveDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/executive/dashboard`, {
        params: { period },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setExecutiveDashboard(response.data);
    } catch (error) {
      console.error('Error fetching executive dashboard:', error);
      toast.error('Failed to load executive dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchKPIs = async () => {
    try {
      const response = await axios.get(`${API}/analytics/executive/kpis`, {
        params: { property_id: selectedProperty?.id },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setKpis(response.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  };

  const fetchCohortAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/cohort-analysis`, {
        params: { months: 6 },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCohortAnalysis(response.data);
    } catch (error) {
      console.error('Error fetching cohort analysis:', error);
      toast.error('Failed to load cohort analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuestLifetimeValue = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/guest-lifetime-value`, {
        params: { limit: 50 },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setGuestLifetimeValue(response.data);
    } catch (error) {
      console.error('Error fetching guest lifetime value:', error);
      toast.error('Failed to load guest lifetime value');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannelAttribution = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/channel-attribution`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setChannelAttribution(response.data);
    } catch (error) {
      console.error('Error fetching channel attribution:', error);
      toast.error('Failed to load channel attribution');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketBasket = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/market-basket-analysis`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMarketBasket(response.data);
    } catch (error) {
      console.error('Error fetching market basket analysis:', error);
      toast.error('Failed to load market basket analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async (metric = 'revenue') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/metrics/trends`, {
        params: { metric, days: 30 },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setTrends(response.data.trend_data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Failed to load trends');
    } finally {
      setLoading(false);
    }
  };

  const fetchBenchmarks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/benchmarking/industry`, {
        params: { property_type: 'hotel' },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setBenchmarks(response.data);
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
      toast.error('Failed to load benchmarks');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomReports = async () => {
    try {
      const response = await axios.get(`${API}/analytics/reports/custom/list`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCustomReports(response.data);
    } catch (error) {
      console.error('Error fetching custom reports:', error);
    }
  };

  const createCustomReport = async () => {
    if (!reportName.trim()) {
      toast.error('Please enter a report name');
      return;
    }

    try {
      await axios.post(`${API}/analytics/reports/custom`, {
        name: reportName,
        report_type: reportType,
        filters: reportFilters
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Custom report created successfully');
      setReportName('');
      setReportFilters({});
      fetchCustomReports();
    } catch (error) {
      console.error('Error creating custom report:', error);
      toast.error('Failed to create custom report');
    }
  };

  const executeReport = async (reportId) => {
    try {
      const response = await axios.post(`${API}/analytics/reports/custom/${reportId}/execute`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Report executed successfully');
      return response.data;
    } catch (error) {
      console.error('Error executing report:', error);
      toast.error('Failed to execute report');
    }
  };

  const fetchWarehouseStatus = async () => {
    try {
      const response = await axios.get(`${API}/analytics/data-warehouse/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setWarehouseStatus(response.data);
    } catch (error) {
      console.error('Error fetching warehouse status:', error);
    }
  };

  const syncDataWarehouse = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/analytics/data-warehouse/sync`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Data warehouse sync initiated');
      setWarehouseStatus(prev => ({
        ...prev,
        last_sync: response.data
      }));
    } catch (error) {
      console.error('Error syncing warehouse:', error);
      toast.error('Failed to sync data warehouse');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('Keyless PMS - Analytics Report', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 30);
    doc.text(`Period: ${period}`, 14, 37);
    
    // Executive Dashboard
    if (executiveDashboard) {
      doc.setFontSize(16);
      doc.text('Executive Dashboard', 14, 50);
      
      const dashboardData = [
        ['Total Revenue', `$${executiveDashboard.total_revenue.toFixed(2)}`],
        ['Occupancy Rate', `${executiveDashboard.occupancy_rate.toFixed(2)}%`],
        ['Average Daily Rate (ADR)', `$${executiveDashboard.adr.toFixed(2)}`],
        ['Revenue Per Available Room (RevPAR)', `$${executiveDashboard.revpar.toFixed(2)}`],
        ['Total Reservations', executiveDashboard.total_reservations],
        ['Guest Satisfaction', `${executiveDashboard.guest_satisfaction}/10`],
        ['Revenue Growth', `${executiveDashboard.revenue_growth}%`]
      ];
      
      doc.autoTable({
        startY: 55,
        head: [['Metric', 'Value']],
        body: dashboardData,
        theme: 'grid'
      });
    }
    
    // KPIs
    if (kpis) {
      doc.addPage();
      doc.setFontSize(16);
      doc.text('Key Performance Indicators', 14, 20);
      
      const kpiData = [
        ['Total Revenue', `$${kpis.total_revenue.toFixed(2)}`],
        ['Total Reservations', kpis.total_reservations],
        ['Cancellation Rate', `${kpis.cancellation_rate.toFixed(2)}%`],
        ['Avg Length of Stay', `${kpis.avg_length_of_stay} days`],
        ['Lead Time', `${kpis.lead_time_days} days`]
      ];
      
      doc.autoTable({
        startY: 25,
        head: [['KPI', 'Value']],
        body: kpiData,
        theme: 'grid'
      });
    }
    
    doc.save('keyless-pms-analytics.pdf');
    toast.success('PDF exported successfully');
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Executive Dashboard Sheet
    if (executiveDashboard) {
      const dashboardData = [
        ['Metric', 'Value'],
        ['Total Revenue', `$${executiveDashboard.total_revenue.toFixed(2)}`],
        ['Occupancy Rate', `${executiveDashboard.occupancy_rate.toFixed(2)}%`],
        ['Average Daily Rate (ADR)', `$${executiveDashboard.adr.toFixed(2)}`],
        ['Revenue Per Available Room (RevPAR)', `$${executiveDashboard.revpar.toFixed(2)}`],
        ['Total Reservations', executiveDashboard.total_reservations],
        ['Guest Satisfaction', `${executiveDashboard.guest_satisfaction}/10`],
        ['Revenue Growth', `${executiveDashboard.revenue_growth}%`]
      ];
      
      const ws1 = XLSX.utils.aoa_to_sheet(dashboardData);
      XLSX.utils.book_append_sheet(workbook, ws1, 'Executive Dashboard');
    }
    
    // KPIs Sheet
    if (kpis) {
      const kpiData = [
        ['KPI', 'Value'],
        ['Total Revenue', `$${kpis.total_revenue.toFixed(2)}`],
        ['Total Reservations', kpis.total_reservations],
        ['Cancellation Rate', `${kpis.cancellation_rate.toFixed(2)}%`],
        ['Avg Length of Stay', `${kpis.avg_length_of_stay} days`],
        ['Lead Time', `${kpis.lead_time_days} days`]
      ];
      
      if (kpis.channel_distribution) {
        kpiData.push(['', '']);
        kpiData.push(['Channel', 'Bookings']);
        Object.entries(kpis.channel_distribution).forEach(([channel, count]) => {
          kpiData.push([channel, count]);
        });
      }
      
      const ws2 = XLSX.utils.aoa_to_sheet(kpiData);
      XLSX.utils.book_append_sheet(workbook, ws2, 'KPIs');
    }
    
    // Guest Lifetime Value Sheet
    if (guestLifetimeValue.length > 0) {
      const glvData = [['Guest Name', 'Total Bookings', 'Total Revenue', 'Avg Booking Value', 'Lifetime Value', 'Predicted Future Value']];
      guestLifetimeValue.forEach(guest => {
        glvData.push([
          guest.guest_name,
          guest.total_bookings,
          `$${guest.total_revenue.toFixed(2)}`,
          `$${guest.avg_booking_value.toFixed(2)}`,
          `$${guest.lifetime_value.toFixed(2)}`,
          `$${guest.predicted_future_value.toFixed(2)}`
        ]);
      });
      
      const ws3 = XLSX.utils.aoa_to_sheet(glvData);
      XLSX.utils.book_append_sheet(workbook, ws3, 'Guest Lifetime Value');
    }
    
    XLSX.writeFile(workbook, 'keyless-pms-analytics.xlsx');
    toast.success('Excel exported successfully');
  };

  useEffect(() => {
    if (activeTab === 'cohort') {
      fetchCohortAnalysis();
    } else if (activeTab === 'lifetime') {
      fetchGuestLifetimeValue();
    } else if (activeTab === 'channels') {
      fetchChannelAttribution();
    } else if (activeTab === 'basket') {
      fetchMarketBasket();
    } else if (activeTab === 'trends') {
      fetchTrends('revenue');
    } else if (activeTab === 'benchmarks') {
      fetchBenchmarks();
    } else if (activeTab === 'reports') {
      fetchCustomReports();
    } else if (activeTab === 'warehouse') {
      fetchWarehouseStatus();
    }
  }, [activeTab]);

  return (
    <div className="flex flex-col gap-4 overflow-y-auto min-h-0 h-full">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Advanced Analytics & BI
          </h1>
          <p className="text-slate-600 mt-2">
            Comprehensive analytics, business intelligence, and data warehouse for {selectedProperty?.name || 'All Properties'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportToPDF} variant="outline" className="gap-2">
            <Download size={18} />
            <span className="hidden sm:inline">Export PDF</span>
          </Button>
          <Button onClick={exportToExcel} variant="outline" className="gap-2">
            <Download size={18} />
            <span className="hidden sm:inline">Export Excel</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-1 h-auto">
          <TabsTrigger value="executive" className="text-xs sm:text-sm">Executive</TabsTrigger>
          <TabsTrigger value="cohort" className="text-xs sm:text-sm">Cohorts</TabsTrigger>
          <TabsTrigger value="lifetime" className="text-xs sm:text-sm">LTV</TabsTrigger>
          <TabsTrigger value="channels" className="text-xs sm:text-sm">Channels</TabsTrigger>
          <TabsTrigger value="basket" className="text-xs sm:text-sm">Basket</TabsTrigger>
          <TabsTrigger value="trends" className="text-xs sm:text-sm">Trends</TabsTrigger>
          <TabsTrigger value="benchmarks" className="text-xs sm:text-sm">Benchmarks</TabsTrigger>
          <TabsTrigger value="reports" className="text-xs sm:text-sm">Reports</TabsTrigger>
          <TabsTrigger value="warehouse" className="text-xs sm:text-sm">Warehouse</TabsTrigger>
        </TabsList>

        {/* Executive Dashboard Tab */}
        <TabsContent value="executive" className="space-y-6">
          <div className="flex items-center gap-4 mb-4">
            <Label>Period:</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Last Week</SelectItem>
                <SelectItem value="month">Last Month</SelectItem>
                <SelectItem value="quarter">Last Quarter</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {executiveDashboard && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                    <DollarSign className="h-4 w-4 text-cyan-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">${executiveDashboard.total_revenue.toFixed(2)}</div>
                    <p className="text-xs text-slate-600 mt-1">
                      <span className={executiveDashboard.revenue_growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {executiveDashboard.revenue_growth >= 0 ? '+' : ''}{executiveDashboard.revenue_growth}%
                      </span> from previous {period}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Occupancy Rate</CardTitle>
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{executiveDashboard.occupancy_rate.toFixed(2)}%</div>
                    <p className="text-xs text-slate-600 mt-1">
                      {executiveDashboard.total_reservations} reservations
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Average Daily Rate</CardTitle>
                    <BarChart3 className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">${executiveDashboard.adr.toFixed(2)}</div>
                    <p className="text-xs text-slate-600 mt-1">
                      RevPAR: ${executiveDashboard.revpar.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Guest Satisfaction</CardTitle>
                    <Award className="h-4 w-4 text-pink-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{executiveDashboard.guest_satisfaction}/10</div>
                    <p className="text-xs text-slate-600 mt-1">
                      Based on reviews
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Additional KPIs */}
              {kpis && (
                <Card>
                  <CardHeader>
                    <CardTitle>Additional KPIs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Cancellation Rate</p>
                        <p className="text-xl font-bold">{kpis.cancellation_rate.toFixed(2)}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Avg Length of Stay</p>
                        <p className="text-xl font-bold">{kpis.avg_length_of_stay} days</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Lead Time</p>
                        <p className="text-xl font-bold">{kpis.lead_time_days} days</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Bookings</p>
                        <p className="text-xl font-bold">{kpis.total_reservations}</p>
                      </div>
                    </div>

                    {kpis.channel_distribution && Object.keys(kpis.channel_distribution).length > 0 && (
                      <div className="mt-6">
                        <p className="text-sm font-medium mb-3">Channel Distribution</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(kpis.channel_distribution).map(([channel, count]) => (
                            <Badge key={channel} variant="outline">
                              {channel}: {count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Cohort Analysis Tab */}
        <TabsContent value="cohort" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Cohort Analysis - Guest Retention
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading cohort analysis...</p>
              ) : cohortAnalysis.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Cohort Month</th>
                        <th className="text-right p-2">Cohort Size</th>
                        <th className="text-right p-2">Month 1</th>
                        <th className="text-right p-2">Month 2</th>
                        <th className="text-right p-2">Month 3</th>
                        <th className="text-right p-2">Revenue/Guest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohortAnalysis.map((cohort, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="p-2 font-medium">{cohort.cohort_month}</td>
                          <td className="p-2 text-right">{cohort.cohort_size}</td>
                          <td className="p-2 text-right">{cohort.retention_rates.month_1}%</td>
                          <td className="p-2 text-right">{cohort.retention_rates.month_2}%</td>
                          <td className="p-2 text-right">{cohort.retention_rates.month_3}%</td>
                          <td className="p-2 text-right">${cohort.revenue_per_guest.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No cohort data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guest Lifetime Value Tab */}
        <TabsContent value="lifetime" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Top Guests by Lifetime Value
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading guest lifetime value...</p>
              ) : guestLifetimeValue.length > 0 ? (
                <div className="space-y-3">
                  {guestLifetimeValue.slice(0, 20).map((guest, idx) => (
                    <div key={guest.guest_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{guest.guest_name}</p>
                          <p className="text-xs text-slate-600">{guest.total_bookings} bookings</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${guest.lifetime_value.toFixed(2)}</p>
                        <p className="text-xs text-slate-600">
                          Avg: ${guest.avg_booking_value.toFixed(2)} | 
                          Predicted: ${guest.predicted_future_value.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No guest data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Channel Attribution Tab */}
        <TabsContent value="channels" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Channel Attribution & Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading channel attribution...</p>
              ) : channelAttribution && channelAttribution.channels ? (
                <div className="space-y-6">
                  {channelAttribution.top_channel && (
                    <div className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg">
                      <p className="text-sm text-slate-600">Top Performing Channel</p>
                      <p className="text-2xl font-bold text-cyan-700">{channelAttribution.top_channel}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(channelAttribution.channels).map(([channel, data]) => (
                      <div key={channel} className="p-4 border rounded-lg">
                        <h3 className="font-semibold text-lg capitalize mb-2">{channel}</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Bookings:</span>
                            <span className="font-medium">{data.bookings}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Revenue:</span>
                            <span className="font-medium">${data.revenue.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Avg Booking Value:</span>
                            <span className="font-medium">${data.avg_booking_value.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pie Chart */}
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={Object.entries(channelAttribution.channels).map(([channel, data]) => ({
                            name: channel,
                            value: data.revenue
                          }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {Object.keys(channelAttribution.channels).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No channel data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Market Basket Analysis Tab */}
        <TabsContent value="basket" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Market Basket Analysis - Upsell Opportunities
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading market basket analysis...</p>
              ) : marketBasket ? (
                <div className="space-y-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Total Transactions Analyzed</p>
                    <p className="text-2xl font-bold">{marketBasket.total_transactions}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Frequent Add-on Combinations</h3>
                    <div className="space-y-3">
                      {Object.entries(marketBasket.frequent_combinations).map(([combo, stats]) => (
                        <div key={combo} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="font-medium capitalize">{combo.replace('_', ' + ')}</span>
                            <Badge>{stats.count} times</Badge>
                          </div>
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="bg-cyan-600 h-2 rounded-full" 
                                  style={{ width: `${stats.confidence * 100}%` }}
                                />
                              </div>
                              <span className="text-sm text-slate-600">{(stats.confidence * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Upsell Recommendations</h3>
                    <div className="space-y-2">
                      {marketBasket.recommendations.map((rec, idx) => (
                        <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{rec.addon_pair}</span>
                            <Badge variant={rec.upsell_potential === 'high' ? 'default' : 'outline'}>
                              {rec.upsell_potential} potential
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No market basket data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Metric Trends (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Button 
                  variant={trends.length > 0 ? "outline" : "default"} 
                  size="sm" 
                  onClick={() => fetchTrends('revenue')}
                >
                  Revenue
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchTrends('occupancy')}
                >
                  Occupancy
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchTrends('adr')}
                >
                  ADR
                </Button>
              </div>

              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading trends...</p>
              ) : trends.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#06b6d4" 
                        strokeWidth={2}
                        name="Value"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">Click a metric to view trends</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Industry Benchmarking
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-8 text-slate-600">Loading benchmarks...</p>
              ) : benchmarks ? (
                <div className="space-y-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Property Type</p>
                    <p className="text-xl font-bold capitalize">{benchmarks.property_type}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(benchmarks.industry_averages).map(([metric, value]) => {
                      const yourValue = benchmarks.your_performance[metric];
                      const isPercentage = metric.includes('rate') || metric.includes('satisfaction');
                      const isBetter = yourValue > value;
                      
                      return (
                        <div key={metric} className="p-4 border rounded-lg">
                          <h3 className="text-sm text-slate-600 mb-2 capitalize">
                            {metric.replace(/_/g, ' ')}
                          </h3>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-xs text-slate-500">Industry Avg</p>
                              <p className="text-lg font-medium">
                                {typeof value === 'number' && value < 100 && !metric.includes('adr') && !metric.includes('revpar') ? 
                                  `${value.toFixed(2)}${isPercentage ? '%' : ''}` : 
                                  `$${value.toFixed(2)}`
                                }
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-500">Your Performance</p>
                              <p className={`text-lg font-bold ${isBetter ? 'text-green-600' : 'text-red-600'}`}>
                                {typeof yourValue === 'number' && yourValue < 100 && !metric.includes('adr') && !metric.includes('revpar') ? 
                                  `${yourValue.toFixed(2)}${isPercentage ? '%' : ''}` : 
                                  `$${yourValue.toFixed(2)}`
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">No benchmark data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Custom Report Builder
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="report-name">Report Name</Label>
                  <Input
                    id="report-name"
                    value={reportName}
                    onChange={(e) => setReportName(e.target.value)}
                    placeholder="Enter report name"
                  />
                </div>

                <div>
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger id="report-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="revenue">Revenue Analysis</SelectItem>
                      <SelectItem value="occupancy">Occupancy Analysis</SelectItem>
                      <SelectItem value="guest">Guest Analysis</SelectItem>
                      <SelectItem value="channel">Channel Performance</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={createCustomReport} className="w-full">
                  Create Report
                </Button>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold mb-4">Saved Reports</h3>
                {customReports.length > 0 ? (
                  <div className="space-y-2">
                    {customReports.map((report) => (
                      <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div>
                          <p className="font-medium">{report.name}</p>
                          <p className="text-xs text-slate-600">
                            {report.report_type} • Created {new Date(report.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => executeReport(report.id)}
                        >
                          Run
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-slate-600">No saved reports yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Data Warehouse Tab */}
        <TabsContent value="warehouse" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Data Warehouse
              </CardTitle>
            </CardHeader>
            <CardContent>
              {warehouseStatus ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                      <p className="text-sm text-slate-600">Warehouse Size</p>
                      <p className="text-2xl font-bold">{warehouseStatus.warehouse_size_gb} GB</p>
                    </div>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-slate-600">Total Records</p>
                      <p className="text-2xl font-bold">{warehouseStatus.total_records.toLocaleString()}</p>
                    </div>
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm text-slate-600">Last Sync</p>
                      <p className="text-lg font-bold">
                        {warehouseStatus.last_sync ? 
                          new Date(warehouseStatus.last_sync.completed_at).toLocaleString() : 
                          'Never'
                        }
                      </p>
                    </div>
                  </div>

                  {warehouseStatus.last_sync && (
                    <Card className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-sm">Last Sync Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-600">Sync Type</p>
                            <p className="font-medium capitalize">{warehouseStatus.last_sync.sync_type}</p>
                          </div>
                          <div>
                            <p className="text-slate-600">Records Synced</p>
                            <p className="font-medium">{warehouseStatus.last_sync.records_synced.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-slate-600">Status</p>
                            <Badge variant={warehouseStatus.last_sync.status === 'completed' ? 'default' : 'outline'}>
                              {warehouseStatus.last_sync.status}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-slate-600">Started At</p>
                            <p className="font-medium text-xs">
                              {new Date(warehouseStatus.last_sync.started_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Button 
                    onClick={syncDataWarehouse} 
                    disabled={loading}
                    className="w-full gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    {loading ? 'Syncing...' : 'Sync Data Warehouse'}
                  </Button>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-600">Loading warehouse status...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedAnalytics;
